import { z } from 'zod'
import { MARKET_KEYS, type MarketKey } from '../zambebet/engine/markets.js'
import type { QuotedMarket } from '../zambebet/engine/quote.js'
import { SLIP_TYPES, type BonusRule, type SelectionStatus, type SlipType } from '../zambebet/slip/types.js'
import type { SelectionRef } from '../zambebet/slip/validate.js'
import { listQuerySchema } from './lists.js'
import type { MatchPhase, MatchStatus } from './matches.js'

/** `pending`: su una partita in corso, in attesa del ritardo live; poi `open` o `rejected` (puntata restituita). */
export const SLIP_STATUSES = ['pending', 'open', 'won', 'lost', 'rejected'] as const
export type SlipStatus = (typeof SLIP_STATUSES)[number]
/** Perché una schedina in attesa è stata annullata alla conferma. */
export type SlipRejectReason = 'oddsChanged' | 'marketClosed'

/**
 * POST /api/tournaments/:id/slips. `odds` è la quota vista dall'ospite per ogni esito: se non è più quella corrente
 * → 409 `oddsChanged` con le quote nuove; un esito non più offerto → 409 `marketClosed` con l'elenco.
 * Un evento senza `matchId` è un antepost (vedi `legKey`).
 */
export const PlaceSlipSchema = z.object({
  type: z.enum(SLIP_TYPES),
  systemSizes: z.array(z.number().int().min(1).max(20)).max(20).optional(),
  stakePerColumn: z.number().int().min(1).max(100_000),
  legs: z
    .array(
      z.object({
        matchId: z.string().trim().min(1).max(64).nullish(),
        banker: z.boolean().default(false),
        selections: z
          .array(
            z.object({
              market: z.enum(MARKET_KEYS),
              subjectId: z.string().trim().min(1).max(64).optional(),
              line: z.number().min(-100).max(100).optional(),
              selection: z.string().trim().min(1).max(64),
              odds: z.number().min(1).max(100),
            }),
          )
          .min(1)
          .max(20),
      }),
    )
    .min(1)
    .max(20),
})
export type PlaceSlip = z.infer<typeof PlaceSlipSchema>

export type SlipSelection = {
  id: string
  market: MarketKey
  subjectId: string | null
  line: number | null
  selection: string
  odds: number
  status: SelectionStatus
  deadHeat: number
}
/** La partita di un evento, con i giocatori per dare un nome agli esiti dei mercati giocatore. */
export type SlipLegMatch = {
  id: string
  phase: MatchPhase
  status: MatchStatus
  homeTeam: { id: string; name: string }
  awayTeam: { id: string; name: string }
  homeScore: number | null
  awayScore: number | null
  players: { id: string; name: string; side: 'home' | 'away' }[]
}
/** `match` null: un antepost; `subjects` dà il nome alle squadre e ai giocatori dei suoi esiti. */
export type SlipLeg = { id: string; position: number; banker: boolean; match: SlipLegMatch | null; subjects?: { id: string; name: string }[]; selections: SlipSelection[] }
export type Slip = {
  id: string
  tournamentId: string
  participantId: string
  type: SlipType
  systemSizes: number[] | null
  stakePerColumn: number
  columns: number
  totalStake: number
  /** Bonus e tetto di vincita congelati alla giocata. */
  bonus: BonusRule[]
  maxPayout: number | null
  status: SlipStatus
  payout: number | null
  /** Quando una schedina in attesa viene confermata o annullata; null se entrata subito. */
  acceptAt: string | null
  rejectReason: SlipRejectReason | null
  placedAt: string
  settledAt: string | null
  legs: SlipLeg[]
}

/**
 * GET /api/tournaments/:id/slips/mine: schedine dell'ospite (più recenti prima) e bilancio (il giocato esclude le
 * annullate). `serverNow` (ISO) per contare il tempo che manca ad `acceptAt` senza fidarsi dell'orologio del client.
 */
export type SlipsMineResponse = { slips: Slip[]; summary: { staked: number; won: number; net: number }; serverNow: string }

/** Corpo dei 409 di POST /slips. */
export type SlipRejection = { error: 'marketClosed'; closed: SelectionRef[] } | { error: 'oddsChanged'; changed: (SelectionRef & { odds: number })[] }

/** GET /api/tournaments/:id/zambebet/quotes?matches=a,b: mercati quotati delle partite in schedina. */
export type ZambeBetQuotesResponse = Record<string, { status: MatchStatus; markets: QuotedMarket[] }>

/** GET /api/tournaments/:id/slips (area riservata, `tournaments.view`): `q` cerca nel nome dell'ospite. */
export const SlipListQuerySchema = listQuerySchema(
  ['placedAt', 'totalStake'],
  {
    matchId: z.string().trim().max(64).optional(),
    type: z.enum(SLIP_TYPES).optional(),
    status: z.enum(SLIP_STATUSES).optional(),
  },
  'desc',
)
export type SlipListQuery = z.infer<typeof SlipListQuerySchema>
export type AdminSlip = Slip & { participantName: string }
